import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const textPresetsKey = ["beheer", "standaardteksten"] as const;

export type TextPreset = components["schemas"]["TextPresetDto"];
export type TextField = components["schemas"]["WedstrijddagTextField"];

/** The API serialises `WedstrijddagTextField` as a number, so the SPA names the three digits once. */
export const TEXT_FIELD = { briefing: 1, reportingTime: 2, remarks: 3 } as const;

/** Mirrors `TextPresetLength.For` on the backend: the UI's own bound is a courtesy. */
const MAX_LENGTH: Record<TextField, number> = {
  [TEXT_FIELD.briefing]: 200,
  [TEXT_FIELD.reportingTime]: 200,
  [TEXT_FIELD.remarks]: 500,
};

export function maxTextLength(field: TextField) {
  return MAX_LENGTH[field];
}

/** The Dutch field name a preset targets, shared with the wedstrijddag form it feeds. */
export function textFieldLabel(field: TextField) {
  switch (field) {
    case TEXT_FIELD.briefing:
      return i18n.t("wedstrijden:boekje.briefing");
    case TEXT_FIELD.reportingTime:
      return i18n.t("wedstrijden:boekje.meldtijd");
    default:
      return i18n.t("wedstrijden:boekje.opmerkingen");
  }
}

/** Same shape as the wedstrijden hooks: the RFC 7807 title is the sentence to show. */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

export function useTextPresets() {
  return useQuery({
    queryKey: textPresetsKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/text-presets");
      if (!data) throw problem(error, response, i18n.t("standaardteksten:errors.ophalen"));
      return data;
    },
  });
}

export function useCreateTextPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { field: TextField; text: string }) => {
      const { data, error, response } = await api.POST("/api/beheer/text-presets", { body });
      if (!data) throw problem(error, response, i18n.t("standaardteksten:errors.toevoegen"));
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: textPresetsKey }),
  });
}

export function useUpdateTextPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { data, error, response } = await api.PUT("/api/beheer/text-presets/{id}", {
        params: { path: { id } },
        body: { text },
      });
      if (!data) throw problem(error, response, i18n.t("standaardteksten:errors.opslaan"));
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: textPresetsKey }),
  });
}

export function useDeleteTextPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/beheer/text-presets/{id}", {
        params: { path: { id } },
      });
      if (error || !response.ok) {
        throw problem(error, response, i18n.t("standaardteksten:errors.verwijderen"));
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: textPresetsKey }),
  });
}
